/**
 * Brand profile (blueprint S04, A03, A04, M11): the single source of truth the
 * AI replies, posters and occasions draw on. A workspace is one brand.
 */

export interface OpeningHours {
  /** 0 = Sunday … 6 = Saturday. */
  weekday: number;
  /** "09:00" – "18:00"; closed when null. */
  open: string | null;
  close: string | null;
}

export interface BrandService {
  id: string;
  name: string;
  /** Minor units are avoided on purpose: this is display data, formatted with Intl. */
  price: number | null;
  currency: string;
  /** Show it in promotions and posters. */
  inPromotions: boolean;
}

export interface BrandLink {
  label: string;
  url: string;
}

/**
 * A day the weekly hours are wrong: a public holiday, a refurbishment, a
 * wedding the whole shop is closed for.
 *
 * Weekly hours alone cannot express this, and the gap is not cosmetic — asked
 * "are you open on Christmas Day?" an agent holding only a weekly table will
 * confidently give the Thursday answer. That is the one failure the whole
 * design exists to prevent, so the exception has to be a field, not a
 * sentence buried in `policies` that nothing can reason about.
 */
export interface BrandClosure {
  /** ISO date, "2026-12-25". */
  date: string;
  /** Closed all day when null; otherwise the hours that replace the weekly ones. */
  open: string | null;
  close: string | null;
  /** Shown to the customer: "Christmas Day", "Staff training". */
  label: string;
}

/**
 * How a customer can pay. A top-three question for a small shop, and until
 * now answerable only from free text in `policies`, which nothing could
 * ground a reply against.
 */
export type PaymentMethod =
  | 'cash'
  | 'card'
  | 'contactless'
  | 'bank_transfer'
  | 'sepa_direct_debit'
  | 'ideal'
  | 'bancontact'
  | 'upi'
  | 'net_banking'
  | 'wallet'
  | 'paypal'
  | 'apple_pay'
  | 'google_pay'
  | 'crypto';

/** Every method, in the order a profile stores them; forms order them by country. */
export const PAYMENT_METHODS: readonly PaymentMethod[] = [
  'cash', 'card', 'contactless', 'bank_transfer', 'sepa_direct_debit',
  'ideal', 'bancontact', 'upi', 'net_banking', 'wallet', 'paypal', 'apple_pay', 'google_pay', 'crypto',
];

/**
 * Whether the business comes to the customer, and how far.
 *
 * `unspecified` and `none` are different answers and must stay that way.
 * `none` is the owner saying "we do not deliver", which is a fact the agent
 * may state. `unspecified` is the owner not having been asked yet, and a
 * fresh profile starts there — otherwise every new workspace would tell its
 * customers it does not deliver, on no authority at all.
 */
export type ServiceAreaKind = 'unspecified' | 'none' | 'local' | 'national' | 'international';
export const SERVICE_AREA_KINDS: readonly ServiceAreaKind[] = [
  'unspecified', 'none', 'local', 'national', 'international',
];

/**
 * Delivery and travel. `delivery` is one of the intents a reply may never be
 * auto-sent on without evidence, and nothing on the profile could provide
 * that evidence — so every delivery question went to a person, forever.
 */
export interface ServiceArea {
  kind: ServiceAreaKind;
  /** Places served, in the owner's words: "Kochi city", "Randstad", "EU". */
  areas: string[];
  /** Free text for the conditions: "free over €50, otherwise €4.95". */
  notes: string;
}

export type BrandTone = 'friendly' | 'warm' | 'formal' | 'playful';
export const BRAND_TONES: readonly BrandTone[] = ['friendly', 'warm', 'formal', 'playful'];

export interface BrandProfile {
  /** Identity */
  name: string;
  tagline: string;
  logoUrl: string | null;
  /** Light-ground logo for dark posters, optional. */
  logoLightUrl: string | null;
  accent: string;
  secondary: string;
  /** Business type in the workspace's own words, e.g. "Hair salon". */
  businessType: string;
  industry: string | null;

  /** Details and hours */
  country: string;
  region: string | null;
  city: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  bookingUrl: string;
  timezone: string;
  hours: OpeningHours[];
  /** Dates the weekly hours do not apply to. Empty is normal. */
  closures: BrandClosure[];

  /** Services and prices */
  services: BrandService[];
  currency: string;
  /** How a customer can pay. Empty means the owner has not said. */
  paymentMethods: PaymentMethod[];
  /** Whether the business travels or delivers, and where. */
  serviceArea: ServiceArea;

  /** Voice and languages */
  tone: BrandTone;
  /** BCP 47 codes the business replies in; the first is the default. */
  languages: string[];
  /** Free text the AI should never contradict: policies, what not to say. */
  policies: string;

  /** Links */
  links: BrandLink[];

  /** Audience */
  audience: string;

  updatedAt: string;
}

/** A section of the wizard, in order. */
export type BrandSection = 'identity' | 'details' | 'services' | 'voice' | 'knowledge' | 'links' | 'team' | 'audience';
export const BRAND_SECTIONS: readonly BrandSection[] = ['identity', 'details', 'services', 'voice', 'knowledge', 'links', 'team', 'audience'];

/** Completeness ring (C19): 0–100 plus what is missing for better answers. */
export interface BrandCompleteness {
  percent: number;
  missing: { section: BrandSection; field: string }[];
}

/** The profile fields an import (A04) can prefill. */
export type BrandImportField = 'name' | 'tagline' | 'logoUrl' | 'address' | 'phone' | 'email' | 'hours' | 'services' | 'accent';
export const BRAND_IMPORT_FIELDS: readonly BrandImportField[] = ['name', 'tagline', 'logoUrl', 'address', 'phone', 'email', 'hours', 'services', 'accent'];

/**
 * What an import (A04) can prefill, from POST /workspaces/me/brand-profile/import.
 * Nothing is saved by the import: the person ticks fields and they go through
 * the normal save. Fields the person edits afterwards are theirs.
 */
export interface BrandImport {
  source: 'website' | 'channel';
  /** Only the fields that were found; a missing key means "not found". */
  fields: Partial<Pick<BrandProfile, BrandImportField>>;
  /** Draft FAQ entries found on the site (schema.org FAQPage), to seed the knowledge base. */
  faq: { question: string; answer: string }[];
  /**
   * The fields this source could have filled but did not. For a channel, these
   * are the ones that would need a live platform call: only what was stored
   * when the channel was connected is read. Optional so older payloads still type.
   */
  unavailable?: BrandImportField[];
}
